import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Users, Shield, BarChart3, Settings, RefreshCw,
  FileText, ScrollText, FileArchive, AlertTriangle, Bell, MessageSquare, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import PageEditor from './PageEditor';
import AdminOverviewTab from './AdminOverviewTab';
import AdminUsersTab from './AdminUsersTab';
import AdminSettingsTab from './AdminSettingsTab';
import AdminLogsTab from './AdminLogsTab';
import AdminAlertsTab from './AdminAlertsTab';
import AdminTicketsTab from './AdminTicketsTab';
import AdminFilesTab from './AdminFilesTab';
import AdminTransfersTab from './AdminTransfersTab';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTabState] = useState(searchParams.get('tab') || 'overview');
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [ticketsOpen, setTicketsOpen] = useState(0);

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast({ title: 'Accès refusé', description: "Vous n'avez pas les droits administrateur", variant: 'destructive' });
        navigate('/');
      }
    } finally {
      setLoadingData(false);
    }
  }, [token, navigate, toast]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/alerts/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertsUnread(res.data.unread || 0);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!loading && !isAuthenticated) { navigate('/'); return; }
    if (!loading && isAuthenticated && !isAdmin) {
      toast({ title: 'Accès refusé', description: 'Cette page est réservée aux administrateurs', variant: 'destructive' });
      navigate('/');
      return;
    }
    if (isAdmin && token) {
      loadData();
      loadUnreadCount();
    }
  }, [isAuthenticated, isAdmin, loading, navigate, token, loadData, loadUnreadCount, toast]);

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const tabs = [
    { id: 'overview', label: "Vue d'ensemble", icon: BarChart3, color: 'blue' },
    { id: 'users', label: 'Utilisateurs', icon: Users, color: 'blue' },
    { id: 'files', label: 'Fichiers', icon: FileArchive, color: 'cyan' },
    { id: 'transfers', label: 'Transferts', icon: Activity, color: 'cyan' },
    { id: 'settings', label: 'Configuration', icon: Settings, color: 'blue' },
    { id: 'pages', label: 'Pages', icon: FileText, color: 'blue' },
    { id: 'logs', label: 'Logs IP', icon: ScrollText, color: 'amber' },
    { id: 'alerts', label: 'Alertes', icon: AlertTriangle, color: 'red', badge: alertsUnread },
    { id: 'tickets', label: 'Tickets', icon: MessageSquare, color: 'emerald', badge: ticketsOpen },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <Toaster />
      <NavigationMenu />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="admin-title">
                  <Shield className="w-6 h-6 text-red-400" />
                  Administration
                </h1>
                <p className="text-blue-300/70 text-sm">Gérez les utilisateurs et l'application</p>
              </div>
            </div>
            <Button onClick={loadData} variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-blue-500/20 pb-4 flex-wrap">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const colorMap = {
                blue: isActive ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : '',
                cyan: isActive ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : '',
                amber: isActive ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : '',
                red: isActive ? 'bg-red-500/20 text-red-300 border border-red-500/30' : '',
                emerald: isActive ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : '',
              };
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all relative ${
                    isActive
                      ? colorMap[tab.color]
                      : 'text-blue-300/60 hover:text-blue-300 hover:bg-slate-800/50'
                  }`}
                  data-testid={`${tab.id}-tab-btn`}
                >
                  <Icon className="w-4 h-4 inline mr-2" />
                  {tab.label}
                  {tab.badge > 0 && (
                    <span className={`absolute -top-1 -right-1 ${tab.color === 'red' ? 'bg-red-500' : 'bg-emerald-500'} text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center`} data-testid={`${tab.id}-unread-badge`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && <AdminOverviewTab stats={stats} />}
          {activeTab === 'users' && <AdminUsersTab token={token} toast={toast} currentUserId={user?.id} />}
          {activeTab === 'files' && <AdminFilesTab token={token} toast={toast} />}
          {activeTab === 'transfers' && <AdminTransfersTab token={token} toast={toast} />}
          {activeTab === 'settings' && <AdminSettingsTab />}
          {activeTab === 'pages' && <PageEditor token={token} toast={toast} />}
          {activeTab === 'logs' && <AdminLogsTab token={token} toast={toast} />}
          {activeTab === 'alerts' && <AdminAlertsTab token={token} toast={toast} onUnreadChange={setAlertsUnread} />}
          {activeTab === 'tickets' && <AdminTicketsTab token={token} toast={toast} />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
